import { action, observable, computed } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type ListInventoryItemsFn } from '@/features/inventory'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { ViewModelList } from '@/infra/view-model-list'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import { ScopeChangeRequest } from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { catchError, EMPTY, from, switchMap, tap } from 'rxjs'
import { shareLatest, whileActive } from '@/utils/rx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Id } from '@/infra/id'
import type { AlertRetryWrapperFactory } from '@/features/shell'

export class AddInventoryItemsToEditDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  /**
   * Search term for item selection
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Selected scope change inventory items
   */
  @observable
  accessor selectedInventoryItems: EditScopeChangeInventoryItemToAddViewModel[] =
    []

  /**
   * The scope change request
   */
  @observable accessor scopeChangeRequest: ScopeChangeRequest =
    ScopeChangeRequest.none

  private readonly _inventoryItems

  readonly inventoryScrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 100,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading service items failed',
        signal,
      )
      return withRetry(() =>
        this._searchInventoryItems(
          {
            workspaceIds: this.scopeChangeRequest.workspace.id
              ? [this.scopeChangeRequest.workspace.id]
              : [],
            limit: paging.limit,
            searchText: this.searchText.value,
            after: paging.after ?? undefined,
            priceTypes: ['Flat'],
            archivedStatus: 'Active',
            jobType: this.scopeChangeRequest.job.jobType,
          },
          signal,
        ),
      )
    },
  })

  /**
   * Server query pipeline.
   */
  private readonly _query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() =>
      from(
        this.inventoryScrollProvider.fetch({ signal: this._lifecycle.signal }),
      ).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      ),
    ),
    shareLatest(),
  )

  constructor(
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _onItemsAdded: (
      itemsToAdd: EditScopeChangeInventoryItemToAddViewModel[],
    ) => void,
  ) {
    this._inventoryItems = new ViewModelList({
      models: () => this.inventoryScrollProvider.items.slice(),
      create: (model) =>
        new EditScopeChangeInventoryItemToAddViewModel({
          itemId: model.id,
          versionId: model.versionId,
          name: model.name,
          description: model.description,
          selected: this.selectedInventoryItems
            .map((i) => i.versionId)
            .includes(model.versionId),
          onSelectedChange: this._handleSelectedChange,
        }),
    })
    this._lifecycle.manage(this.modal.show$.pipe(tap(() => this.reset())))
    this._lifecycle.manage(this._query$.pipe(whileActive(this.modal.showing$)))
    this._lifecycle.manage(this._inventoryItems)
  }

  @action.bound
  reset() {
    this.searchText.clear()
  }

  @action.bound
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.selectedInventoryItems = []
    this.modal.show()
  }

  @computed
  get serviceItems() {
    return this._inventoryItems.items
  }

  @action.bound
  addItems() {
    this._onItemsAdded(this.selectedInventoryItems)
    this.modal.close()
  }

  @action.bound
  private _handleSelectedChange(
    item: EditScopeChangeInventoryItemToAddViewModel,
  ) {
    if (item.selected) {
      this.selectedInventoryItems = [item, ...this.selectedInventoryItems]
    } else {
      this.selectedInventoryItems = this.selectedInventoryItems.filter(
        (i) => i.versionId !== item.versionId,
      )
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}

/**
 * A service item offered for adding to a scope change request.
 */
export interface EditScopeChangeInventoryItemToAddViewModelOptions {
  itemId: Id
  versionId: Id
  name: string
  description: string
  selected: boolean
  onSelectedChange: (item: EditScopeChangeInventoryItemToAddViewModel) => void
}

export class EditScopeChangeInventoryItemToAddViewModel {
  @observable accessor selected: boolean

  constructor(
    private readonly _options: EditScopeChangeInventoryItemToAddViewModelOptions,
  ) {
    this.selected = _options.selected
  }

  get itemId() {
    return this._options.itemId
  }

  get versionId() {
    return this._options.versionId
  }

  get name() {
    return this._options.name
  }

  get description() {
    return this._options.description
  }

  @action.bound
  toggleSelected() {
    this.selected = !this.selected
    this._options.onSelectedChange(this)
  }
}
